// ─── Social sign-in (browser side) ────────────────────────────────────────────
// Google hands us a signed ID token which the server verifies. Nothing here is
// trusted: the token is the only thing that crosses to the backend, and the
// account it identifies is decided there.
//
// The SDK is loaded on demand rather than on every page load — it is a
// third-party script on the critical path of a page most visitors never sign
// in from.

const GOOGLE_SRC = 'https://accounts.google.com/gsi/client';

const loaded = new Map();

/** Load a third-party script once, reusing the same promise for repeat clicks. */
function loadScript(src) {
  if (loaded.has(src)) return loaded.get(src);

  const promise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load')));
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.defer = true;
    el.onload  = () => { el.dataset.loaded = 'true'; resolve(); };
    el.onerror = () => reject(new Error('Failed to load'));
    document.head.appendChild(el);
  });

  // A failed load must not be cached, or the button stays broken until reload.
  promise.catch(() => loaded.delete(src));
  loaded.set(src, promise);
  return promise;
}

/**
 * Which providers this deployment can actually honour. Asking the server keeps
 * us from showing a button that is guaranteed to fail — the client ids live in
 * the backend environment, not the bundle.
 * @returns {Promise<string[]>}
 */
export async function availableProviders() {
  try {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/auth/providers`);
    if (!res.ok) return [];
    const { providers } = await res.json();
    return Array.isArray(providers) ? providers : [];
  } catch {
    return [];   // offline or the endpoint is older than this build
  }
}

/**
 * Render Google's own sign-in button into `container`.
 *
 * Deliberately not One Tap (`prompt()`): it is suppressed whenever the visitor
 * has dismissed it before, has no Google session, or blocks third-party
 * cookies — so a button wired to it silently does nothing for a large share of
 * people. The rendered button always works on click, and Google's branding
 * terms want their mark used anyway.
 *
 * @returns {Promise<void>} resolves once the button is on screen
 */
export async function renderGoogleButton(container, { clientId, onCredential, onError, width }) {
  if (!container) return;
  try {
    await loadScript(GOOGLE_SRC);
    const google = window.google;
    if (!google?.accounts?.id) throw new Error('unavailable');

    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response?.credential) onCredential({ provider: 'google', idToken: response.credential });
        else onError?.(new Error('Google did not return a sign-in token.'));
      },
      auto_select: false,
    });

    container.innerHTML = '';
    google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'filled_black',
      size: 'large',
      text: 'continue_with',
      shape: 'pill',
      logo_alignment: 'center',
      width: Math.round(width || container.offsetWidth || 360),
    });
  } catch {
    onError?.(new Error('Google sign-in could not load. Check your connection and try again.'));
  }
}

